import React from "react";
import styled from "styled-components";
import { useBlogPage } from "./hook";
import { Pagination } from "../../organisms/Pagination";
import { Link } from "../../atoms/Link";
import { COLOR_BLOG_BG } from "../../../constants/color";
import { PageRoutes } from "../../../constants/routes";
import { buildImageUrl } from "../../../utils/imageUrl";
import { useLanguage } from '../../../contexts/LanguageContext';
import dayjs from 'dayjs';

export const BlogPage = () => {
  const { blogs, isLoading, page, onSetPage, onPrevious, onNext, hasPagination } = useBlogPage();
  const { t } = useLanguage();

  return (
    <PageWrapper>
      <ContentWrapper>
        <MainSection>
          <SectionTitle>{t('blog.articles')}</SectionTitle>
          {!isLoading && blogs.data && blogs.data.length === 0 ? (
            <EmptyMsg>{t('blog.noBlogAvailable') || 'No blog available'}</EmptyMsg>
          ) : (
            <>
              <BlogList>
                {blogs.data.map((blog) => (
                  <BlogCard key={blog.id}>
                    <BlogContent>
                      <BlogTitle>
                        <Link to={PageRoutes.BLOG_DETAIL.replace(":id", String(blog.id))}>{blog.title}</Link>
                      </BlogTitle>
                      <BlogSnippet>
                        <div dangerouslySetInnerHTML={{ __html: blog.content.replace(/<[^>]+>/g, '').slice(0, 120) + '...' }} />
                      </BlogSnippet>
                      <BlogMeta>
                        <span>{dayjs(blog.created_at).format('DD MMMM YYYY')}</span>
                      </BlogMeta>
                    </BlogContent>
                    {blog.image && (
                      <BlogImageWrapper>
                        <BlogImage src={buildImageUrl(blog.image)} alt={blog.title} />
                      </BlogImageWrapper>
                    )}
                  </BlogCard>
                ))}
              </BlogList>
              {hasPagination && (
                <PaginationWrapper>
                  <Pagination
                    page={page}
                    totalPage={blogs.total_pages}
                    onSetPage={onSetPage}
                    onPrevious={onPrevious}
                    onNext={onNext}
                  />
                </PaginationWrapper>
              )}
            </>
          )}
        </MainSection>
        <Sidebar>
          <SidebarBlock>
            <SidebarTitle>{t('blog.recentArticles')}</SidebarTitle>
            <SidebarList>
              {blogs && blogs.data && blogs.data.length > 0 && blogs.data.slice(0, 5).map((blog) => (
                <SidebarListItem key={blog.id}>
                  <Link to={PageRoutes.BLOG_DETAIL.replace(":id", String(blog.id))}>{blog.title}</Link>
                </SidebarListItem>
              ))}
            </SidebarList>
          </SidebarBlock>
        </Sidebar>
      </ContentWrapper>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 1200px;
  margin: 16px auto 0 auto;
  width: 100%;
  gap: 24px;
  flex: 1;
  padding: 0 12px;
  
  @media (min-width: 400px) {
    padding: 0 16px;
    gap: 28px;
    margin: 20px auto 0 auto;
  }
  
  @media (min-width: 768px) {
    padding: 0 24px;
    gap: 32px;
    margin: 24px auto 0 auto;
  }
  
  @media (min-width: 900px) {
    padding: 0 32px;
    margin: 32px auto 0 auto;
  }
  
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const MainSection = styled.div`
  flex: 3;
  background: #fff;
  border-radius: 8px;
  padding: 16px 16px 24px;
  min-width: 0;
  
  @media (min-width: 768px) {
    padding: 20px 20px 28px;
  }
  
  @media (min-width: 900px) {
    padding: 0 24px 32px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    font-size: 20px;
    margin-bottom: 24px;
  }
`;

const BlogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (min-width: 768px) {
    gap: 18px;
  }
`;

const BlogCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${COLOR_BLOG_BG};
  border-radius: 8px;
  padding: 16px;
  gap: 12px;
  align-items: flex-start;
  box-shadow: 0 1px 4px rgba(0,0,0,0.03);
  
  @media (min-width: 768px) {
    flex-direction: row;
    padding: 18px;
    gap: 18px;
  }
`;

const BlogImageWrapper = styled.div`
  width: 100%;
  height: 120px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #eaeaea;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (min-width: 768px) {
    width: 110px;
    height: 80px;
  }
`;

const BlogImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BlogContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
  }
`;

const BlogTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #18145a;
  margin-bottom: 2px;
  
  @media (min-width: 768px) {
    font-size: 15px;
  }
`;

const BlogSnippet = styled.div`
  font-size: 12px;
  color: #444;
  margin-bottom: 4px;
  
  @media (min-width: 768px) {
    font-size: 13px;
  }
`;

const BlogMeta = styled.div`
  font-size: 11px;
  color: #888;
  
  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const Sidebar = styled.aside`
  flex: 1;
  min-width: 240px;
  max-width: 320px;
  background: #fff;
  border-radius: 8px;
  padding: 20px 16px;
  background: ${COLOR_BLOG_BG};
  height: fit-content;
  
  @media (min-width: 768px) {
    padding: 24px 18px;
  }
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const SidebarBlock = styled.div`
  margin-bottom: 16px;
  
  @media (min-width: 768px) {
    margin-bottom: 18px;
  }
`;

const SidebarTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  
  @media (min-width: 768px) {
    font-size: 15px;
    margin-bottom: 10px;
  }
`;

const SidebarList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SidebarListItem = styled.li`
  font-size: 12px;
  color: #222;
  margin-bottom: 6px;
  
  @media (min-width: 768px) {
    font-size: 13px;
    margin-bottom: 8px;
  }
  
  a {
    color: #18145a;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;



const EmptyMsg = styled.div`
  color: #888;
  margin: 20px 0;
  text-align: center;
  
  @media (min-width: 768px) {
    margin: 24px 0;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  
  @media (min-width: 768px) {
    margin-top: 32px;
  }
`;
